import { AdditionalInterestAndInsured } from 'src/configs/APISchemas/Application.schema';
import {
  UpdateApplicationResponse,
  formatAdditionalPerson,
  getBuildingQueryString,
} from 'src/configs/APISchemas/UpdateApplication';
import { GraphQLRequest } from 'src/helpers/GraphQLRequest';
import { InterestedParty } from 'src/pages/payment-msi/PaymentMsiV2/types';
import { PurchasePolicyInput } from 'src/redux/reducer/v2/purchasePolicy';

interface UpdateAdditionalPartiesInput {
  additionalInsured: AdditionalInterestAndInsured[];
  interestedParty: InterestedParty | null;
  buildingId: string | undefined;
  buildingName: string | undefined;
  buildingAddress: PurchasePolicyInput['address'] | undefined;
  landlordData: InterestedParty | null;
  communityId: string | null;
  applicationId: string;
}

export async function updateAdditionalParties({
  additionalInsured,
  applicationId,
  buildingId,
  buildingName,
  buildingAddress,
  communityId,
  interestedParty,
  landlordData,
}: UpdateAdditionalPartiesInput) {
  const interestedPartyData = getInterestedPartyData({
    interestedParty,
    landlordData,
  });

  const mutation = getUpdateAdditionalPartiesMutation({
    additionalInsuredRoommates: additionalInsured,
    additionalInterests: interestedPartyData ? [interestedPartyData] : [],
    buildingAddress,
    buildingId,
    buildingName,
    communityId,
    applicationId,
  });

  const response = await GraphQLRequest<UpdateApplicationResponse>(mutation);

  return response.updateApplication;
}

function getInterestedPartyData({
  interestedParty,
  landlordData,
}: {
  interestedParty: InterestedParty | null;
  landlordData: InterestedParty | null;
}): AdditionalInterestAndInsured | null {
  if (!landlordData) {
    if (!interestedParty) {
      return null;
    }

    const { email, fullName } = interestedParty;

    const [firstName, lastName] = fullName.split(' ');

    return {
      email,
      firstName: firstName ?? '',
      lastName: lastName ?? '',
    };
  }

  return {
    email: landlordData.email,
    firstName: landlordData.fullName ?? '',
    lastName: '.',
  };
}

interface GetUpdateAdditionalPartiesMutationInput {
  applicationId: string;
  communityId: string | null;
  buildingAddress: PurchasePolicyInput['address'] | undefined;
  buildingId: string | undefined;
  buildingName: string | undefined;
  additionalInsuredRoommates: AdditionalInterestAndInsured[];
  additionalInterests: AdditionalInterestAndInsured[];
}
function getUpdateAdditionalPartiesMutation({
  additionalInsuredRoommates,
  additionalInterests,
  buildingAddress,
  buildingId,
  buildingName,
  communityId,
  applicationId,
}: GetUpdateAdditionalPartiesMutationInput) {
  const buildingQueryString = getBuildingQueryString({
    address: buildingAddress,
    buildingId,
    buildingName,
  });
  const communityIdQueryString = communityId
    ? `communityId: "${communityId}",`
    : '';
  const additionalInsuredRoommatesQueryString =
    getAdditionalInsuredRoommatesQueryString(additionalInsuredRoommates);
  const additionalInterestsQueryString =
    getAdditionalInterestsQueryString(additionalInterests);

  return `
    mutation {
      updateApplication(
        applicationId: "${applicationId}",
        input: {
          ${communityIdQueryString}
          ${buildingQueryString}
          ${additionalInsuredRoommatesQueryString}
          ${additionalInterestsQueryString}
        }
      ) {
        ... on ApplicationOutput {
          id
          buildingId
        }
        ... on ErrorListOutput {
          errors {
            type
            message
          }
        }
      }
    }
    `;
}

const getAdditionalInsuredRoommatesQueryString = (
  additionalInsuredRoommates: AdditionalInterestAndInsured[],
) => {
  if (!additionalInsuredRoommates || additionalInsuredRoommates.length === 0) {
    return `additionalInsuredRoommates: [],`;
  }

  return `additionalInsuredRoommates: [${additionalInsuredRoommates
    .map((person) => formatAdditionalPerson(person))
    .join(',')}],`;
};

const getAdditionalInterestsQueryString = (
  additionalInterests: AdditionalInterestAndInsured[],
) => {
  if (!additionalInterests || additionalInterests.length === 0) {
    return `additionalInterests: [],`;
  }

  return `additionalInterests: [${additionalInterests
    .map((person) => formatAdditionalPerson(person))
    .join(',')}],`;
};
